import React, {
  FunctionComponent,
  SyntheticEvent,
  useState,
  useCallback,
  useEffect,
} from 'react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFormContext } from 'react-hook-form';

import { ClassProp } from 'helpers/types';
import { Theme } from 'config';
import FormSelect from 'components/FormSelect';
import { DropDown } from 'components/FormSelect/FormSelect.helpers';

import FormErrors from '../FormError';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  name: string;
  dropDownOptions: DropDown;
  label?: string;
  onSelectChange?: (value: any) => void;
  size?: string;
  width?: number;
  defaultValue?: Option;
  value?: Option;
  tabIndex?: number;
  multiple?: boolean;
  disabled?: boolean;
  wrapSelection?: boolean;
  search?: boolean;
  loading?: boolean;
  required?: boolean;
};

type Option =
  | string
  | number
  | boolean
  | (string | number | boolean)[]
  | undefined
  | null;

type SelectedOption = {
  name: string;
  value: string | number | boolean | any;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '32%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  inline: {
    width: '31% !important',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {},
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  selectList: {
    margin: '0 !important',
    '& div': {
      '&.ui.selection.dropdown': {
        padding: '.5641rem 1rem !important;',
        fontSize: '1rem !important',
        lineHeight: '1.2em',
        '& .default.text': {
          color: 'rgba(0, 0, 0, .25) !important',
        },
      },
      '&.ui.multiple.selection.dropdown': {
        padding: '.178rem 1rem !important',
        '& a.ui.label': {
          margin: '.25rem !important',
          fontSize: '.85rem !important',
          fontWeight: 'normal !important',
          backgroundColor: theme.softViolet,
        },
      },
    },
  },
});

const FormSelectList: FunctionComponent<Props> = ({
  classes,
  label,
  name,
  dropDownOptions,
  defaultValue,
  value,
  onSelectChange,
  size,
  tabIndex,
  disabled = false,
  loading,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState<Option>(defaultValue);

  const { errors, setValue, trigger } = useFormContext();

  useEffect(() => {
    if (defaultValue) {
      setInternalValue(defaultValue);
      setValue(name, defaultValue);
    }
  }, [disabled, defaultValue]);

  useEffect(() => {
    if (value) {
      setInternalValue(value);
      setValue(name, value);
    }
  }, [value]);

  const handleChange = useCallback(
    (
      _: SyntheticEvent<HTMLElement, Event>,
      { name, value }: SelectedOption,
    ) => {
      setInternalValue(value);
      setValue(name, value);
      trigger(name);
      if (onSelectChange) onSelectChange(value);
    },
    [internalValue, setInternalValue, onSelectChange],
  );

  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
        [classes.inline]: size === 'inline',
      })}
    >
      <label htmlFor={name}>
        {label && <span className={classes.fieldLabel}>{label}</span>}
        <FormSelect
          disabled={disabled}
          className={classes.selectList}
          fluid
          loading={loading}
          selectOnBlur={false}
          name={name}
          placeholder={dropDownOptions.placeholder}
          options={dropDownOptions.options}
          onChange={handleChange}
          value={internalValue}
          tabIndex={tabIndex}
          {...rest}
        />
      </label>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};

export default withStyles(styles)(FormSelectList);
